import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useBoolState } from 'nallian-shared-ui/lib/hooks';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import {
  Modal,
  Dimmer,
  Loader,
  Grid,
  Button,
  ConfirmModal,
  Flex,
} from 'nallian-shared-ui/lib/components';
import { JOIN_REQUEST_ACTIONS } from '../../constants';
import joinRequestService from '../../services/joinRequests/joinRequestService';
import denyJoinRequestService from '../../services/joinRequests/denyJoinRequestService';
import HeaderDetail, { HeaderGrid, HeaderColumn } from '../../../app/components/HeaderDetail';
import ModalHeader from '../../../app/components/ModalHeader';
import Language from '../../../app/components/Language';
import useModalInModal from '../../../app/hooks/useModalInModal';

const MANAGE_RIGHTS = [
  'PRINCIPALJOINREQUESTS.MANAGE.ALL',
  'PRINCIPALJOINREQUESTS.MANAGE.ORGANISATION',
];

const JoinRequestDetail = ({
  open,
  rootUrl,
  organisationId,
  dispatchJoinRequestGet,
  joinRequestFetch,
  dispatchDenyJoinRequestPost,
  denyJoinRequestFetch,
}) => {
  const { t } = useTranslation();
  const { actionId } = useParams();
  const navigate = useNavigate();

  const [modalActive, setModalActive, unsetModalActive] = useBoolState(false);

  const handleModalClose = useCallback(() => {
    unsetModalActive();
    navigate(rootUrl);
  }, [rootUrl, unsetModalActive, navigate]);

  const handleDenyClick = useCallback(
    () =>
      dispatchDenyJoinRequestPost(organisationId, actionId, handleModalClose, handleModalClose, t),
    [t, actionId, organisationId, handleModalClose, dispatchDenyJoinRequestPost]
  );

  const handleAcceptClick = useCallback(
    () =>
      navigate(
        `${rootUrl}/join-requests/${JOIN_REQUEST_ACTIONS.ACCEPT_JOIN_REQUEST}/${actionId}`
      ),
    [rootUrl, actionId, navigate]
  );

  useEffect(() => {
    if (open && actionId) {
      dispatchJoinRequestGet(organisationId, actionId, handleModalClose, t);
    }
  }, [t, open, organisationId, actionId, dispatchJoinRequestGet, handleModalClose]);

  useModalInModal(modalActive);

  if (!open) return null;

  return (
    <Modal open closeIcon onClose={handleModalClose}>
      <Dimmer active={joinRequestFetch.pending} inverted>
        <Loader />
      </Dimmer>
      <Modal.Header>{t('reviewJoinRequest')}</Modal.Header>
      <Modal.Content>
        <Flex fluid direction="column">
          <Flex margin="-0.5rem 0 0">
            <ModalHeader content={t('user')} />
          </Flex>
          <HeaderGrid columns={1}>
            <HeaderColumn>
              <Grid divided="vertically" columns="equal">
                <HeaderDetail
                  title={t('fullName')}
                  content={joinRequestFetch.value?.principal?.fullName}
                />
              </Grid>
            </HeaderColumn>
            <HeaderColumn>
              <Grid divided="vertically" columns="equal">
                <HeaderDetail
                  title={t('emailAddress')}
                  content={joinRequestFetch.value?.principal?.primaryEmailAddress}
                />
                <HeaderDetail
                  title={t('phoneNumber')}
                  content={joinRequestFetch.value?.principal?.primaryPhoneNumber}
                />
              </Grid>
            </HeaderColumn>
            <HeaderColumn>
              <Grid divided="vertically" columns="equal">
                <HeaderDetail
                  title={t('language')}
                  content={<Language value={joinRequestFetch.value?.principal?.languageId} />}
                />
                <HeaderDetail
                  title={t('timezone')}
                  content={joinRequestFetch.value?.principal?.timeZone}
                />
              </Grid>
            </HeaderColumn>
          </HeaderGrid>
        </Flex>
      </Modal.Content>
      <HasSomeRights rights={MANAGE_RIGHTS}>
        <Modal.Actions>
          <ConfirmModal
            header={t('areYouSureYouWantToDenyJoinRequest')}
            cancelContent={t('cancel')}
            confirmContent={t('deny')}
            confirmColor="red"
            onConfirm={handleDenyClick}
            trigger={
              <Button
                content={t('deny')}
                title={t('deny')}
                icon="cancel"
                onClick={setModalActive}
                disabled={joinRequestFetch.pending || denyJoinRequestFetch.pending}
                color="red"
              />
            }
          />
          <Button
            content={t('accept')}
            title={t('accept')}
            icon="check"
            onClick={handleAcceptClick}
            disabled={joinRequestFetch.pending || denyJoinRequestFetch.pending}
            color="green"
          />
        </Modal.Actions>
      </HasSomeRights>
    </Modal>
  );
};

JoinRequestDetail.propTypes = {
  dispatchJoinRequestGet: PropTypes.func.isRequired,
  joinRequestFetch: PropTypes.object.isRequired,
  dispatchDenyJoinRequestPost: PropTypes.func.isRequired,
  denyJoinRequestFetch: PropTypes.object.isRequired,
  rootUrl: PropTypes.string.isRequired,
  organisationId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
};

export default compose(denyJoinRequestService, joinRequestService)(JoinRequestDetail);
